export type OrderStatus =
  | 'in_progress'
  | 'completed'
  | 'failed'
  | 'pending'
  | 'preview'
  // v2 HeyGen chat/plan session that's still in the review flow
  // (user hasn't approved the plan yet, so no video is rendering).
  | 'chat_active';

export interface Order {
  orderId: string;
  orderName: string;
  orderStatus: OrderStatus;
  orderDate: string;
  operationType: string;
  videoUrl?: string;
  outputUrl?: string;  // The final downloadable URL (uploaded to our S3)
  errorMessage?: string;
  folderId: string;
  userId: string;
  completedAt?: string;
  falVideoId?: string;  // Fal.ai video ID for remix feature
  falModel?: string;  // Fal.ai model used for this order (e.g., "fal-ai/veo3.1/fast")
  previewImageUrl?: string;  // Preview image for free user orders
  isPreview?: boolean;  // Flag indicating this is a preview order (free user)
  videoProvider?: string;  // 'heygen' for HeyGen orders, absent for fal.ai
  fallbackFrom?: string;  // 'longvid_v1' | 'videogen_v1' when that provider's create fell back to HeyGen
  generationMode?: string;  // 'auto' for one-shot HeyGen (vs the agentic chat flow)
  createdByName?: string;  // Team workspaces: which member created this (attribution)
}

export interface OrderResponse {
  order_id: string;
  order_name?: string;
  order_status: string;
  order_date?: string;
  created_date?: string;
  order_type?: string;
  operation_type?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  result_data?: Record<string, any>;  // Flexible structure for different API responses
  video_url?: string;
  output_url?: string;  // The final downloadable URL (uploaded to our S3)
  error_message?: string;
  folder_id?: string;
  user_id: string;
  completed_at?: string;
  fal_video_id?: string;  // Fal.ai video ID for remix feature
  fal_model?: string;  // Fal.ai model used for this order (e.g., "fal-ai/veo3.1/fast")
  preview_image_url?: string;  // Preview image for free user orders
  cover_image_url?: string;  // V2 free-funnel blurred cover (v2-plan-cover stores it on the order)
  is_preview?: boolean;  // Flag indicating this is a preview order (free user)
  video_provider?: string;  // 'heygen' for HeyGen orders, absent for fal.ai
  fallback_from?: string;  // 'longvid_v1' | 'videogen_v1' when that provider's create fell back to HeyGen
  generation_mode?: string;  // 'auto' for one-shot HeyGen orders
  created_by?: string;       // Team workspaces: creator's user_id (org-owned orders)
  created_by_name?: string;  // Team workspaces: creator's display name
}

export function parseOrder(data: OrderResponse): Order {
  // Debug: Log raw order data to identify video_id location
  if (data.result_data) {
    console.log(`[parseOrder] Order ${data.order_id} result_data:`, JSON.stringify(data.result_data, null, 2));
  }
  if (data.fal_video_id) {
    console.log(`[parseOrder] Order ${data.order_id} fal_video_id:`, data.fal_video_id);
  }

  // Extract video URL from result_data (fallback for preview)
  let videoUrl = data.video_url;
  if (!videoUrl && data.result_data) {
    videoUrl = data.result_data.video?.url 
      || data.result_data.audio?.url 
      || data.result_data.images?.[0]?.url;
  }

  // output_url is the final downloadable URL (uploaded to our S3)
  const outputUrl = data.output_url;

  // Extract fal video ID for remix feature (Sora 2 videos)
  // Check multiple possible locations where the video_id might be stored
  let falVideoId = data.fal_video_id;
  if (!falVideoId && data.result_data) {
    // Try different possible locations in result_data
    // Sora 2 returns: { video: { url, id } }
    falVideoId = data.result_data.video?.id 
      || data.result_data.video_id  // Some APIs return video_id at root
      || data.result_data.id;       // Some might just have id at root
    
    if (falVideoId) {
      console.log(`[parseOrder] Extracted falVideoId from result_data:`, falVideoId);
    }
  }

  return {
    orderId: data.order_id,
    orderName: data.order_name || 'Untitled',
    orderStatus: (data.order_status?.toLowerCase() as OrderStatus) || 'pending',
    orderDate: data.order_date || data.created_date || new Date().toISOString(),
    operationType: data.order_type || data.operation_type || '',
    videoUrl,
    outputUrl,
    errorMessage: data.error_message,
    folderId: data.folder_id || data.user_id,
    userId: data.user_id,
    completedAt: data.completed_at,
    falVideoId,
    falModel: data.fal_model,
    // V2 preview orders store their blurred cover as cover_image_url — fall back to
    // it so the projects grid shows the poster (V1's preview_image_url still wins).
    previewImageUrl: data.preview_image_url || data.cover_image_url,
    isPreview: data.is_preview || data.order_status?.toLowerCase() === 'preview',
    videoProvider: data.video_provider,
    fallbackFrom: data.fallback_from,
    generationMode: data.generation_mode,
    createdByName: data.created_by_name,
  };
}

export const OPERATION_TYPES = {
  TEXT_TO_VIDEO: 'text_to_video',
  IMAGE_TO_VIDEO: 'image_to_video',
  VIDEO_REMIX: 'video_remix',  // Video remix using Sora 2
  VIDEO_EXTEND: 'video_extend',  // Extend video using Veo 3.1
  VIDEO_EDIT_WAN: 'video_edit_wan',  // Edit video using Wan
  FIRST_FRAME_LAST_FRAME: 'first_frame_last_frame',
  VIDEO_TO_VIDEO: 'video_to_video',
  VIDEO_TO_AUDIO: 'video_to_audio',
  REMOVE_VIDEO_BACKGROUND: 'remove_video_background',
  UPSCALE_VIDEO: 'upscale_video',
  LIPSYNC: 'lipsync',
  AUTO_CAPTION: 'auto_caption',
  DUBBING: 'dubbing',
  AI_AVATAR: 'ai_avatar',
  EFFECTS: 'effects',
  REFRAME_ASPECT_RATIO: 'reframe_aspect_ratio',
  MULTIPLE_IMAGES: 'multiple_images',
  REFERENCE_TO_VIDEO: 'reference_to_video',
} as const;

export type OperationType = typeof OPERATION_TYPES[keyof typeof OPERATION_TYPES];

